import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const SelectionContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'pont10_session_id';
const LOCAL_BACKUP_KEY = 'pont10_selection_backup';

// Generate a unique session ID
const generateSessionId = () => {
  return 'sess_' + Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Get or create session ID
const getOrCreateSessionId = () => {
  let sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    localStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
};

export const SelectionProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [sessionId] = useState(getOrCreateSessionId);
  const syncTimeoutRef = useRef(null);

  // Load cart from backend on mount
  useEffect(() => {
    const loadCart = async () => {
      try {
        const response = await fetch(`${API_URL}/api/cart/${sessionId}`);
        if (response.ok) {
          const cart = await response.json();
          if (cart.items && cart.items.length > 0) {
            // Transform backend items to local format
            const localItems = cart.items.map(item => ({
              id: item.product_id,
              cartItemId: item.id, // Backend item ID for updates
              name: { es: item.product_name, en: item.product_name, cat: item.product_name },
              price: item.price,
              image: '',
              categoryId: '',
              isShopItem: item.is_shop_item || false,
              quantity: item.quantity,
              selectedOptions: item.options || [],
              notes: item.notes || '',
              addedAt: new Date(item.added_at).getTime()
            }));
            setItems(localItems);
          } else {
            // Try to load from local backup (migration from old localStorage)
            const backup = localStorage.getItem(LOCAL_BACKUP_KEY);
            if (backup) {
              const parsed = JSON.parse(backup);
              if (parsed.length > 0) {
                setItems(parsed);
                // Sync to backend
                syncToBackend(parsed);
                // Clear backup after migration
                localStorage.removeItem(LOCAL_BACKUP_KEY);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading cart from backend:', error);
        // Fallback to local backup
        const backup = localStorage.getItem(LOCAL_BACKUP_KEY);
        if (backup) {
          setItems(JSON.parse(backup));
        }
      }
    };

    loadCart();
  }, [sessionId]);

  // Sync items to backend (debounced)
  const syncToBackend = useCallback(async (itemsToSync) => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(async () => {
      setSyncing(true);
      try {
        // Clear backend cart first
        await fetch(`${API_URL}/api/cart/${sessionId}`, { method: 'DELETE' });

        // Add all items
        for (const item of itemsToSync) {
          const itemName = typeof item.name === 'object' ? (item.name.es || item.name.en || '') : item.name;
          await fetch(`${API_URL}/api/cart/${sessionId}/items`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              product_id: item.id,
              product_name: itemName,
              price: item.price,
              quantity: item.quantity,
              options: item.selectedOptions || [],
              notes: item.notes || '',
              is_shop_item: item.isShopItem || false
            })
          });
        }
      } catch (error) {
        console.error('Error syncing to backend:', error);
      } finally {
        setSyncing(false);
      }
    }, 500); // Debounce 500ms
  }, [sessionId]);

  // Save local backup and sync on changes
  useEffect(() => {
    // Always save local backup
    localStorage.setItem(LOCAL_BACKUP_KEY, JSON.stringify(items));
    
    // Sync to backend
    if (items.length >= 0) {
      syncToBackend(items);
    }
  }, [items, syncToBackend]);

  // Add item to selection
  const addItem = useCallback((product, quantity = 1, selectedOptions = []) => {
    setItems(prev => {
      // Check if item with same options already exists
      const existingIndex = prev.findIndex(
        item => item.id === product.id && 
        JSON.stringify(item.selectedOptions) === JSON.stringify(selectedOptions)
      );

      if (existingIndex >= 0) {
        // Update quantity
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity
        };
        return updated;
      }

      // Add new item
      return [...prev, {
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image_url || product.images?.[0] || '',
        categoryId: product.category_id,
        isShopItem: product.is_shop_item || product.category_id?.startsWith('cat-tienda') || false,
        quantity,
        selectedOptions,
        addedAt: Date.now()
      }];
    });

    // Trigger animation feedback
    setLastAdded(product.id);
    setTimeout(() => setLastAdded(null), 10000); // Keep for 10 seconds for suggestions
  }, []);

  // Remove item from selection
  const removeItem = useCallback((itemId, selectedOptions = []) => {
    setItems(prev => prev.filter(
      item => !(item.id === itemId && 
        JSON.stringify(item.selectedOptions) === JSON.stringify(selectedOptions))
    ));
  }, []);

  // Update item quantity
  const updateQuantity = useCallback((itemId, selectedOptions, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(itemId, selectedOptions);
      return;
    }

    setItems(prev => prev.map(item => {
      if (item.id === itemId && 
          JSON.stringify(item.selectedOptions) === JSON.stringify(selectedOptions)) {
        return { ...item, quantity: newQuantity };
      }
      return item;
    }));
  }, [removeItem]);

  // Bulk replace items. Used by the Guided Flow to push its locally-managed
  // drink/food selections into the shared cart so they persist when the user
  // switches over to Carta Libre and vice versa.
  const replaceItems = useCallback((newItems) => {
    const normalized = (newItems || []).map(it => ({
      id: it.id,
      cartItemId: it.cartItemId,
      name: it.name,
      price: it.price,
      image: it.image || it.image_url || '',
      categoryId: it.categoryId || it.category_id || '',
      isShopItem: it.isShopItem || false,
      quantity: it.quantity ?? it.qty ?? 1,
      selectedOptions: it.selectedOptions || [],
      notes: it.notes || '',
      addedAt: it.addedAt || Date.now(),
    }));
    setItems(normalized);
  }, []);

  // Clear all items
  const clearSelection = useCallback(async () => {
    setItems([]);
    // Also clear on backend
    try {
      await fetch(`${API_URL}/api/cart/${sessionId}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Error clearing cart on backend:', error);
    }
  }, [sessionId]);

  // Calculate total
  const getTotal = useCallback(() => {
    return items.reduce((sum, item) => {
      const optionsExtra = item.selectedOptions?.reduce(
        (acc, opt) => acc + (opt.price_modifier || 0), 0
      ) || 0;
      return sum + (item.price + optionsExtra) * item.quantity;
    }, 0);
  }, [items]);

  // Get items separated by type (local vs shop)
  const getLocalItems = useCallback(() => {
    return items.filter(item => !item.isShopItem);
  }, [items]);

  const getShopItems = useCallback(() => {
    return items.filter(item => item.isShopItem);
  }, [items]);

  // Get totals by type
  const getLocalTotal = useCallback(() => {
    return getLocalItems().reduce((sum, item) => {
      const optionsExtra = item.selectedOptions?.reduce(
        (acc, opt) => acc + (opt.price_modifier || 0), 0
      ) || 0;
      return sum + (item.price + optionsExtra) * item.quantity;
    }, 0);
  }, [getLocalItems]);

  const getShopTotal = useCallback(() => {
    return getShopItems().reduce((sum, item) => {
      const optionsExtra = item.selectedOptions?.reduce(
        (acc, opt) => acc + (opt.price_modifier || 0), 0
      ) || 0;
      return sum + (item.price + optionsExtra) * item.quantity;
    }, 0);
  }, [getShopItems]);

  // Get item count
  const getItemCount = useCallback(() => {
    return items.reduce((sum, item) => sum + item.quantity, 0);
  }, [items]);

  // Check if item is in selection
  const isInSelection = useCallback((productId) => {
    return items.some(item => item.id === productId);
  }, [items]);

  // Get unique category IDs from selection (for cross-sell)
  const getCategoryIds = useCallback(() => {
    return [...new Set(items.map(item => item.categoryId).filter(Boolean))];
  }, [items]);

  // Get session ID for order creation
  const getSessionId = useCallback(() => sessionId, [sessionId]);

  // Generate WhatsApp message
  const generateWhatsAppMessage = useCallback((language = 'es') => {
    const localItems = getLocalItems();
    const shopItems = getShopItems();
    
    const messages = {
      es: {
        header: '🍷 *Mi selección en Pont10*\n\n',
        localHeader: '🍽️ *Consumo en local:*\n',
        shopHeader: '🛍️ *Para llevar:*\n',
        item: (name, qty, price) => `• ${qty}x ${name} - ${price.toFixed(2)}€\n`,
        total: (total) => `\n*Total estimado:* ${total.toFixed(2)}€\n`,
        footer: '\n_Esta selección no es un pedido confirmado._'
      },
      en: {
        header: '🍷 *My selection at Pont10*\n\n',
        localHeader: '🍽️ *Dine-in:*\n',
        shopHeader: '🛍️ *Take away:*\n',
        item: (name, qty, price) => `• ${qty}x ${name} - ${price.toFixed(2)}€\n`,
        total: (total) => `\n*Estimated total:* ${total.toFixed(2)}€\n`,
        footer: '\n_This selection is not a confirmed order._'
      },
      cat: {
        header: '🍷 *La meva selecció a Pont10*\n\n',
        localHeader: '🍽️ *Consum al local:*\n',
        shopHeader: '🛍️ *Per emportar:*\n',
        item: (name, qty, price) => `• ${qty}x ${name} - ${price.toFixed(2)}€\n`,
        total: (total) => `\n*Total estimat:* ${total.toFixed(2)}€\n`,
        footer: '\n_Aquesta selecció no és una comanda confirmada._'
      }
    };

    const msg = messages[language] || messages.es;
    let text = msg.header;

    // Local items section
    if (localItems.length > 0) {
      text += msg.localHeader;
      localItems.forEach(item => {
        const name = item.name?.[language] || item.name?.es || item.name;
        const optionsExtra = item.selectedOptions?.reduce(
          (acc, opt) => acc + (opt.price_modifier || 0), 0
        ) || 0;
        const itemTotal = (item.price + optionsExtra) * item.quantity;
        text += msg.item(name, item.quantity, itemTotal);
        
        if (item.selectedOptions?.length > 0) {
          item.selectedOptions.forEach(opt => {
            const optName = opt.name?.[language] || opt.name?.es || opt.name;
            text += `   ↳ ${optName}\n`;
          });
        }
      });
      text += '\n';
    }

    // Shop items section
    if (shopItems.length > 0) {
      text += msg.shopHeader;
      shopItems.forEach(item => {
        const name = item.name?.[language] || item.name?.es || item.name;
        const optionsExtra = item.selectedOptions?.reduce(
          (acc, opt) => acc + (opt.price_modifier || 0), 0
        ) || 0;
        const itemTotal = (item.price + optionsExtra) * item.quantity;
        text += msg.item(name, item.quantity, itemTotal);
      });
      text += '\n';
    }

    text += msg.total(getTotal());
    text += msg.footer;

    return encodeURIComponent(text);
  }, [getLocalItems, getShopItems, getTotal]);

  return (
    <SelectionContext.Provider value={{
      items,
      isOpen,
      setIsOpen,
      lastAdded,
      syncing,
      sessionId,
      addItem,
      removeItem,
      updateQuantity,
      clearSelection,
      replaceItems,
      getTotal,
      getLocalTotal,
      getShopTotal,
      getLocalItems,
      getShopItems,
      getItemCount,
      isInSelection,
      getCategoryIds,
      getSessionId,
      generateWhatsAppMessage
    }}>
      {children}
    </SelectionContext.Provider>
  );
};

export const useSelection = () => {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
};
